import { useState, useCallback, useEffect } from "react";
import { Badge, Switch } from "@earth-note/ui";
import { RiLeafLine } from "@remixicon/react";
import { GifPreview } from "./GifPreview";
import { useEmailBody } from "../hooks/useEmailBody";
import type { AddinConfigEnabled } from "../types";

interface ActiveUserProps {
  config: AddinConfigEnabled;
}

export function ActiveUser({ config }: ActiveUserProps) {
  const [toggleOn, setToggleOn] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const { injectGif, removeGif, setGifEnabled, error: bodyError } = useEmailBody();

  // ---------------------------------------------------------------------------
  // Task 1 + Task 2 — On task pane load:
  //   • Inject the GIF into this email's compose body (Task 1)
  //   • Set X-EarthNote-Include: true so the SMTP relay attaches the GIF
  //     signature when this email is sent (Task 2)
  // Both operations are scoped to THIS compose item only. The toggle starts ON
  // by default, so we initialise the email in the "included" state immediately.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setIsProcessing(true);
    // Pass the libraryId (config.gif.id) so the relay can attribute usage
    // to the exact library shown in the compose preview, not a random
    // re-fetch from the rotation pool.
    Promise.all([injectGif(config.gif), setGifEnabled(true, config.gif.id)])
      .catch((err: unknown) => {
        console.error("[EarthNote] Failed to initialise GIF on task pane load:", err);
      })
      .finally(() => {
        setIsProcessing(false);
      });
    // Run once on mount — injectGif/setGifEnabled are stable useCallback refs,
    // config.gif is the same object for the lifetime of this compose window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(async () => {
    const newState = !toggleOn;
    setToggleOn(newState);
    setIsProcessing(true);

    try {
      if (newState) {
        // Toggle ON → inject GIF into body + tell SMTP relay to include signature
        // (with libraryId so the relay logs usage against the correct library)
        await Promise.all([injectGif(config.gif), setGifEnabled(true, config.gif.id)]);
      } else {
        // Toggle OFF → remove GIF from body + tell SMTP relay to skip signature
        await Promise.all([removeGif(config.gif), setGifEnabled(false)]);
      }
    } catch (err) {
      setToggleOn(!newState); // Revert UI on error
      console.error("[EarthNote] Failed to update GIF state:", err);
    } finally {
      setIsProcessing(false);
    }
  }, [toggleOn, injectGif, removeGif, setGifEnabled, config.gif]);

  return (
    <div className="flex h-full flex-col p-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-title-h6 font-semibold text-text-strong-950">Earth Note</h1>
          <p className="text-paragraph-xs text-text-soft-400">{config.org.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
            <RiLeafLine className="h-5 w-5 text-primary-600" />
          </div>
          <Badge.Root color="green" variant="light" size="small">
            <Badge.Dot />
            Active
          </Badge.Root>
        </div>
      </div>

      {/* Toggle card */}
      <div className="mb-6 space-y-2 rounded-12 border border-stroke-soft-200 p-4">
        <div className="flex items-center justify-between">
          <span className="text-label-sm font-medium text-text-strong-950">
            Include GIF in this email
          </span>
          <Switch.Root
            checked={toggleOn}
            onCheckedChange={() => void handleToggle()}
            disabled={isProcessing}
            aria-label="Toggle GIF inclusion"
          />
        </div>
        <p className="text-paragraph-xs text-text-soft-400">
          {toggleOn ? "GIF will be added below your signature" : "GIF will not be included"}
        </p>
      </div>

      {/* Error message */}
      {bodyError && (
        <div className="mb-4 rounded-8 border border-red-200 bg-red-50 p-3">
          <p className="text-paragraph-xs text-red-600">{bodyError}</p>
        </div>
      )}

      {/* GIF Preview */}
      {toggleOn && (
        <div className="mb-6">
          <GifPreview gif={config.gif} />
        </div>
      )}

      {/* Status indicator */}
      <div className="mt-auto flex items-center gap-2">
        <div className={`h-2 w-2 rounded-full ${toggleOn ? "bg-green-500" : "bg-bg-sub-300"}`} />
        <span className="text-paragraph-xs text-text-soft-400">
          {toggleOn ? "Active for this email" : "Inactive for this email"}
        </span>
      </div>

      {/* Footer */}
      <p className="mt-4 text-center text-paragraph-xs text-text-soft-400">
        Hi {config.user.firstName}! You&apos;re helping spread climate awareness.
      </p>
    </div>
  );
}
